import type { RefObject } from 'react'
import { useRef } from 'react'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

interface UseClearSelectionOnOutsidePressProps {
  rootRef: RefObject<HTMLElement>
  onClearSelection: () => void
  ignoredSelectors?: string[]
}

const DEFAULT_IGNORED_SELECTORS = [
  '[role="dialog"]',
  '[role="menu"]',
  '[data-radix-portal]',
  '[data-radix-popper-content-wrapper]',
]

const isNode = (value: EventTarget | null): value is Node => value instanceof Node

const isInsideIgnoredArea = (target: Node, ignoredSelectors: string[]): boolean => {
  const targetElement = target instanceof Element ? target : target.parentElement
  if (!targetElement) return false

  return ignoredSelectors.some((selector) => !!targetElement.closest(selector))
}

const useClearSelectionOnOutsidePress = ({
  rootRef,
  onClearSelection,
  ignoredSelectors = DEFAULT_IGNORED_SELECTORS,
}: UseClearSelectionOnOutsidePressProps): void => {
  const onClearSelectionRef = useRef(onClearSelection)
  const ignoredSelectorsRef = useRef(ignoredSelectors)

  onClearSelectionRef.current = onClearSelection
  ignoredSelectorsRef.current = ignoredSelectors

  useUnsafeEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const root = rootRef.current
      const target = event.target
      if (!root || !isNode(target)) return
      if (root.contains(target)) return
      if (isInsideIgnoredArea(target, ignoredSelectorsRef.current)) return

      onClearSelectionRef.current()
    }

    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [rootRef])
}

export { useClearSelectionOnOutsidePress }
